import {
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useReducedMotion } from "framer-motion";
import { HighlightContext } from "../contexts/highlightContextValue";
import {
  ANIMATION_DURATIONS,
  HIGHLIGHT_SWEEP_EASING,
} from "../constants/animations";
import { MARKER_INK, resolveMarkerColor } from "../utils/highlightColor";
import { hashSeed, markerBackground, markerShape } from "../utils/markerShape";

interface HighlightSpanProps {
  /** Hex from the `{#hex}` suffix; omit for the section's default colour. */
  color?: string;
  /** Seeds this phrase's stroke shape (see markerShape); usually its text. */
  seed?: string;
  children: ReactNode;
}

/**
 * Sideways overshoot of the band past the text, in em. Padding adds it and the
 * equal negative margin takes it back, so the text never moves; slice
 * fragmentation applies both only at the span's true start and end.
 */
const OVERSHOOT = "0.2em";

/**
 * A `==highlighted==` phrase: a translucent band under the text that sweeps in
 * left to right like a highlighter pen.
 *
 * The band is a background image on an inline span, not an overlay element, so
 * it wraps across line breaks with the text and cannot move layout or line
 * height (a background never does, and the padding is cancelled by the
 * margin). `background-size` is what animates. With the default
 * `box-decoration-break: slice` the browser lays the background along the
 * phrase's fragments end to end, so a wrapped phrase draws line one and then
 * line two instead of every line at once.
 *
 * The group decides when to draw (see HighlightContext). The sweep plays once:
 * leaving the group unmounts the span, and a group that skipped its own reveal
 * hands over `static` so the band is simply there.
 */
export function HighlightSpan({
  color,
  seed = "",
  children,
}: HighlightSpanProps) {
  const { phase, palette } = useContext(HighlightContext);
  const prefersReducedMotion = useReducedMotion();
  const instant = phase === "static" || Boolean(prefersReducedMotion);
  // pending -> sweeping (band drawing) -> settled (sweep over). Only ever
  // advanced by the timers below; `instant` skips straight to the end.
  const [stage, setStage] = useState<"pending" | "sweeping" | "settled">(
    "pending",
  );

  useEffect(() => {
    if (instant || phase !== "play") return;
    const { highlightSweepDelay, highlightSweep, highlightInkSettle } =
      ANIMATION_DURATIONS;
    const timers = [
      window.setTimeout(() => setStage("sweeping"), highlightSweepDelay),
      window.setTimeout(
        () => setStage("settled"),
        highlightSweepDelay + highlightSweep + highlightInkSettle,
      ),
    ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [instant, phase]);

  // Default highlights are yellow with dark ink; authored colours keep the
  // text colour (and a flatter stroke, see markerShape).
  const inked = !color;
  const band = useMemo(
    () => resolveMarkerColor(color, palette),
    [color, palette],
  );
  const shape = useMemo(
    () => markerShape(hashSeed(seed), inked),
    [seed, inked],
  );

  const current = instant ? "settled" : stage;
  const drawn = current !== "pending";
  // While sweeping (and before it starts) the dark ink is a text-clipped layer
  // that reveals with the band, so letters stay readable until it reaches them.
  const revealing = inked && current !== "settled";
  const bg = markerBackground(
    band,
    shape,
    drawn,
    revealing ? MARKER_INK : undefined,
  );

  return (
    <span
      data-highlight={drawn ? "drawn" : "pending"}
      style={{
        ...bg,
        backgroundRepeat: "no-repeat",
        // Glyphs are painted by the clipped layers (white, then ink).
        ...(revealing
          ? {
              WebkitTextFillColor: "transparent",
              WebkitBackgroundClip: bg.backgroundClip,
            }
          : { color: inked ? MARKER_INK : undefined }),
        transition:
          current === "settled"
            ? "none"
            : `background-size ${ANIMATION_DURATIONS.highlightSweep}ms ${HIGHLIGHT_SWEEP_EASING}, background-position ${ANIMATION_DURATIONS.highlightSweep}ms ${HIGHLIGHT_SWEEP_EASING}`,
        padding: `0.06em ${OVERSHOOT}`,
        margin: `0 -${OVERSHOOT}`,
        // Slice is the default; stated because number pills beside it use clone.
        boxDecorationBreak: "slice",
        WebkitBoxDecorationBreak: "slice",
      }}
    >
      {children}
    </span>
  );
}
